import { useState, useEffect, useCallback, useMemo } from "react";

/**
 * Hardcoded list of applications that can be filtered.
 * The `applicationName` must match exactly what's stored in `entry.application`.
 */
export const FILTERABLE_APPLICATIONS = [
  { id: "outlook", displayName: "Outlook", applicationName: "Microsoft Outlook" },
  { id: "word", displayName: "Word", applicationName: "Microsoft Word" },
  { id: "excel", displayName: "Excel", applicationName: "Microsoft Excel" },
  { id: "powerpoint", displayName: "PowerPoint", applicationName: "Microsoft Powerpoint" },
  { id: "adobe", displayName: "Adobe Acrobat", applicationName: "Adobe Acrobat" },
  { id: "kofax", displayName: "Kofax PDF", applicationName: "Kofax" },
  { id: "foxit", displayName: "Foxit PDF", applicationName: "Foxit" },
] as const;

export type FilterableAppId = typeof FILTERABLE_APPLICATIONS[number]["id"];

// Special application values that should ALWAYS be shown (manual entries, combined, etc.)
const ALWAYS_VISIBLE_APPLICATIONS = [
  "Combine",
  "Duplicate",
  "User Input",
  "",
];

interface UseApplicationFilterReturn {
  /** Array of app IDs that are currently hidden */
  hiddenApplications: FilterableAppId[];

  /** Toggle visibility of a specific app by ID */
  toggleApplication: (appId: FilterableAppId) => void;

  /** Show all applications (clear all filters) */
  showAll: () => void;

  /** Hide all applications */
  hideAll: () => void;

  /** Check if a specific app is visible (for Switch component) */
  isVisible: (appId: FilterableAppId) => boolean;

  /** Filter an entries array - removes entries from hidden applications */
  filterEntries: <T extends { application?: string }>(entries: T[]) => T[];

  /** Whether any filter is currently active */
  isFilterActive: boolean;

  /** The list of filterable applications (for rendering UI) */
  filterableApplications: typeof FILTERABLE_APPLICATIONS;
}

const STORAGE_KEY_PREFIX = "hourglass_app_filter_";

function getStorageKey(userEmail: string): string {
  // Sanitize email for use as storage key
  const sanitized = userEmail.replace(/[^a-zA-Z0-9@._-]/g, "_");
  return `${STORAGE_KEY_PREFIX}${sanitized}`;
}

function loadFromStorage(userEmail: string): FilterableAppId[] {
  if (!userEmail) return [];

  try {
    const key = getStorageKey(userEmail);
    const stored = localStorage.getItem(key);
    if (!stored) return [];

    const parsed = JSON.parse(stored);
    if (Array.isArray(parsed.hiddenApplications)) {
      // Validate that all IDs are valid
      const validIds = FILTERABLE_APPLICATIONS.map(app => app.id);
      return parsed.hiddenApplications.filter((id: string) => validIds.includes(id as FilterableAppId));
    }
    return [];
  } catch {
    return [];
  }
}

function saveToStorage(userEmail: string, hiddenApplications: FilterableAppId[]): void {
  if (!userEmail) return;

  try {
    const key = getStorageKey(userEmail);
    localStorage.setItem(key, JSON.stringify({ hiddenApplications }));
  } catch {
    // Storage quota exceeded or other error - fail silently
  }
}

/**
 * Hook for managing application visibility filter.
 * Persists filter state to localStorage scoped by user email.
 *
 * @param userEmail - The current user's email (for localStorage scoping)
 */
export function useApplicationFilter(userEmail: string): UseApplicationFilterReturn {
  const [hiddenApplications, setHiddenApplications] = useState<FilterableAppId[]>(() =>
    loadFromStorage(userEmail)
  );

  // Reload from storage when userEmail changes (different user logged in)
  useEffect(() => {
    setHiddenApplications(loadFromStorage(userEmail));
  }, [userEmail]);

  // Persist to storage whenever hiddenApplications changes
  useEffect(() => {
    saveToStorage(userEmail, hiddenApplications);
  }, [userEmail, hiddenApplications]);

  const toggleApplication = useCallback((appId: FilterableAppId) => {
    setHiddenApplications(prev =>
      prev.includes(appId)
        ? prev.filter(id => id !== appId)  // Remove from hidden (show the app)
        : [...prev, appId]                  // Add to hidden (hide the app)
    );
  }, []);

  const showAll = useCallback(() => {
    setHiddenApplications([]);
  }, []);

  const hideAll = useCallback(() => {
    setHiddenApplications(FILTERABLE_APPLICATIONS.map(app => app.id));
  }, []);

  const isVisible = useCallback((appId: FilterableAppId): boolean => {
    return !hiddenApplications.includes(appId);
  }, [hiddenApplications]);

  // Build a Set of hidden application names for efficient lookup
  const hiddenAppNames = useMemo(() => {
    const names = new Set<string>();
    for (const appId of hiddenApplications) {
      const app = FILTERABLE_APPLICATIONS.find(a => a.id === appId);
      if (app) {
        names.add(app.applicationName);
      }
    }
    return names;
  }, [hiddenApplications]);

  const filterEntries = useCallback(<T extends { application?: string }>(entries: T[]): T[] => {
    // If no filter active, return all entries
    if (hiddenApplications.length === 0) {
      return entries;
    }

    return entries.filter(entry => {
      const app = entry.application;

      // Always show entries without an application or with special application values
      if (!app || ALWAYS_VISIBLE_APPLICATIONS.includes(app)) {
        return true;
      }

      // Check if this app is in the filterable list
      const isFilterable = FILTERABLE_APPLICATIONS.some(fa => fa.applicationName === app);

      // If not filterable (e.g., Chrome, Zoom), always show
      if (!isFilterable) {
        return true;
      }

      // If filterable and hidden, don't show
      if (hiddenAppNames.has(app)) {
        return false;
      }

      // Otherwise show
      return true;
    });
  }, [hiddenApplications.length, hiddenAppNames]);

  const isFilterActive = hiddenApplications.length > 0;

  return {
    hiddenApplications,
    toggleApplication,
    showAll,
    hideAll,
    isVisible,
    filterEntries,
    isFilterActive,
    filterableApplications: FILTERABLE_APPLICATIONS,
  };
}
